import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { currentUser, refreshUser } from '../lib/auth';

/**
 * Email verification landing page.
 *
 * Flow:
 *   1. User clicks the verification link in their inbox.
 *   2. Backend processes the token server-side and redirects here with
 *      `?verified=1` on success. (If the token is bad it redirects with
 *      no query param — we treat the absence as "still pending".)
 *   3. We call `refreshUser()` so the cached User picks up
 *      `emailVerified: true`, then show a "you're good to go" banner.
 *
 * v1 is deliberately a soft gate: protected routes do NOT require
 * `emailVerified` so the user can use the app immediately and verify when
 * convenient. This page is therefore informational, not blocking.
 */
export function VerifyEmail() {
  const [params] = useSearchParams();
  const verified = params.get('verified') === '1';

  // We only refresh once when arriving in the verified state — the page is
  // small and the user shouldn't sit here long enough for staleness to matter.
  const [refreshing, setRefreshing] = useState(verified);
  useEffect(() => {
    let alive = true;
    if (verified) {
      refreshUser().finally(() => {
        if (alive) setRefreshing(false);
      });
    }
    return () => {
      alive = false;
    };
  }, [verified]);

  const user = currentUser();
  // Recompute after the refresh so the success copy reflects the freshest
  // server state — not the stale cache from before we hit /auth/me.
  const isVerified = verified && !refreshing && (user?.emailVerified ?? true);

  return (
    <div className="min-h-screen relative grid place-items-center px-5 overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0  opacity-80" />
      <div aria-hidden="true" className="absolute inset-x-0 top-1/4 h-[400px] " />

      <div className="relative w-full max-w-[460px]">
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="w-8 h-8 rounded-md bg-ink text-paper grid place-items-center font-extrabold text-[15px] leading-none" aria-hidden="true">T</span>
          <span className="font-bold tracking-tight text-[20px] text-ink">Thren</span>
        </div>

        <div className="surface p-7">
          {verified ? (
            <>
              <div
                role="status"
                className="rounded-md border border-candy-lime/30 bg-candy-lime/10 text-candy-lime px-3 py-2 text-[12.5px]"
              >
                Email verified.
              </div>
              <h1 className="mt-4 text-[22px] font-bold tracking-tight">
                You&rsquo;re all set
              </h1>
              <p className="mt-2 text-[14px] text-muted leading-relaxed">
                {isVerified
                  ? 'Your email is confirmed. Head back to the dashboard to pick up where you left off.'
                  : 'Refreshing your account…'}
              </p>
              <div className="mt-5">
                <Link to="/dashboard" className="btn-primary">
                  Continue to dashboard
                </Link>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-[22px] font-bold tracking-tight">
                Check your inbox
              </h1>
              <p className="mt-2 text-[14px] text-muted leading-relaxed">
                {user?.email ? (
                  <>
                    We sent a verification link to{' '}
                    <span className="text-ink">{user.email}</span>. Click it
                    to confirm your account — you can keep using Thren while
                    you wait.
                  </>
                ) : (
                  <>
                    We sent you a verification link. Click it to confirm your
                    account — you can keep using Thren while you wait.
                  </>
                )}
              </p>
              <div className="mt-5 flex items-center gap-3">
                <Link to="/dashboard" className="btn-primary">
                  Go to dashboard
                </Link>
                <Link to="/account" className="btn-ghost">
                  Account settings
                </Link>
              </div>
              <p className="mt-5 pt-4 border-t border-rule text-[11.5px] text-muted leading-relaxed">
                Didn&rsquo;t get the email? Check your spam folder, or update
                your address from your account settings.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
